import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Plus, Edit, Trash2, DollarSign, Star, Mail, Phone, Send } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

import axios, { API } from './apiClient';

// Auto-attach Bearer token for cross-origin auth
axios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const CommissionsTab = ({ salesReps, commissions, commissionTiers, commissionSummary, commissionChartData, customers, onSave }) => {
  const [showRepModal, setShowRepModal] = useState(false);
  const [showCommModal, setShowCommModal] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [expandedRep, setExpandedRep] = useState(null);
  const [repEarnings, setRepEarnings] = useState(null);
  const [showAdminConfig, setShowAdminConfig] = useState(false);
  const [adminOverride, setAdminOverride] = useState({ rep_id: '', percentage: 20 });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [newNote, setNewNote] = useState('');
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonuses, setBonuses] = useState([]);
  const [bonusForm, setBonusForm] = useState({ sales_rep_id: '', amount: '', date: '', reason: '' });
  
  const [repForm, setRepForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', ssn: '',
    home_address: '', city: '', state: '', zip_code: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    start_date: '', quit_date: '', is_active: true, is_admin: false, override_percentage: 0,
    username: '', password: '', role: 'sales'
  });
  const [commForm, setCommForm] = useState({ sales_rep_id: '', customer_id: '', deal_value: '', commission_month: '', notes: '' });
  const [editTiers, setEditTiers] = useState(false);
  const [tiersForm, setTiersForm] = useState(commissionTiers);
  const [chartView, setChartView] = useState('monthly');
  const [activeRepTab, setActiveRepTab] = useState('info');

  useEffect(() => { setTiersForm(commissionTiers); }, [commissionTiers]);

  const fetchBonuses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/bonuses`, {});
      setBonuses(res.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Error fetching bonuses:', e); }
  }, []);

  useEffect(() => { fetchBonuses(); }, [fetchBonuses]);

  // Memoize expensive bonus total calculation
  const totalBonusesPaid = useMemo(
    () => bonuses.reduce((sum, b) => sum + (b.amount || 0), 0),
    [bonuses]
  );

  // Memoize per-rep monthly sales totals (avoids recalc on every render)
  const repMonthlySalesTotals = useMemo(
    () => salesReps.reduce((acc, rep) => {
      acc[rep.id] = Object.values(rep.monthly_sales || {}).reduce((a, b) => a + b, 0);
      return acc;
    }, {}),
    [salesReps]
  );

  const handleAddBonus = async () => {
    if (!bonusForm.sales_rep_id || !bonusForm.amount || !bonusForm.date || !bonusForm.reason) {
      alert('Please fill in all bonus fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/bonuses`, {
        ...bonusForm,
        amount: parseFloat(bonusForm.amount)
      }, {});
      setShowBonusModal(false);
      setBonusForm({ sales_rep_id: '', amount: '', date: '', reason: '' });
      fetchBonuses();
      onSave();
    } catch (e) {
      alert('Error adding bonus');
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  const handleDeleteBonus = async (salesRepId, bonusId) => {
    if (!window.confirm('Are you sure you want to delete this bonus?')) return;
    try {
      await axios.delete(`${API}/admin/bonuses/${salesRepId}/${bonusId}`, {});
      fetchBonuses();
      onSave();
    } catch (e) {
      alert('Error deleting bonus');
      if (process.env.NODE_ENV === 'development') console.error(e);
    }
  };

  const getRepName = (rep) => `${rep.first_name || ''} ${rep.last_name || ''}`.trim() || rep.name || 'Unknown';

  const handleSaveRep = async () => {
    // Validate required fields
    if (!repForm.first_name || !repForm.first_name.trim()) {
      alert('First Name is required');
      return;
    }
    if (!repForm.last_name || !repForm.last_name.trim()) {
      alert('Last Name is required');
      return;
    }
    if (!repForm.email || !repForm.email.trim()) {
      alert('Email is required');
      return;
    }
    
    try {
      if (editingRep) {
        await axios.put(`${API}/admin/sales-reps/${editingRep.id}`, repForm, {});
      } else {
        await axios.post(`${API}/admin/sales-reps`, repForm, {});
      }
      setShowRepModal(false);
      resetRepForm();
      setEditingRep(null);
      onSave();
    } catch (e) { 
      if (process.env.NODE_ENV === 'development') console.error(e); 
      const errorMsg = e.response?.data?.detail || 'Error saving sales rep';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const resetRepForm = () => setRepForm({
    first_name: '', last_name: '', email: '', phone: '', ssn: '',
    home_address: '', city: '', state: '', zip_code: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    start_date: '', quit_date: '', is_active: true, is_admin: false, override_percentage: 0,
    username: '', password: '', role: 'sales'
  });

  const handleDeleteRep = async (id) => {
    if (process.env.NODE_ENV === 'development') console.log('Delete clicked for rep ID:', id);
    if (!window.confirm('Are you sure you want to delete this sales rep? This cannot be undone.')) {
      if (process.env.NODE_ENV === 'development') console.log('Delete cancelled by user');
      return;
    }
    try {
      if (process.env.NODE_ENV === 'development') console.log('Sending delete request...');
      const response = await axios.delete(`${API}/admin/sales-reps/${id}`, {});
      if (process.env.NODE_ENV === 'development') console.log('Delete response:', response.data);
      if (expandedRep === id) setExpandedRep(null);
      onSave();
      alert('Sales rep deleted successfully!');
    } catch (e) {
      if (process.env.NODE_ENV === 'development') { console.error('Delete error:', e); console.error('Error response:', e.response?.data); }
      alert('Error deleting sales rep: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleExpandRep = async (repId) => {
    if (expandedRep === repId) {
      setExpandedRep(null);
      setRepEarnings(null);
      return;
    }
    setExpandedRep(repId);
    setActiveRepTab('info');
    try {
      const res = await axios.get(`${API}/admin/sales-reps/${repId}/earnings`, {});
      setRepEarnings(res.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); }
  };

  const handleSetAdmin = async () => {
    if (!adminOverride.rep_id) { alert('Select a sales rep'); return; }
    try {
      await axios.put(`${API}/admin/admin-override/${adminOverride.rep_id}`, { override_percentage: adminOverride.percentage }, {});
      setShowAdminConfig(false);
      onSave();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); alert('Error setting admin'); }
  };

  const handleRemoveAdmin = async (repId) => {
    if (!window.confirm('Remove admin status from this rep?')) return;
    try {
      await axios.delete(`${API}/admin/admin-override/${repId}`, {});
      onSave();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); }
  };

  const handleAddNote = async (repId) => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`${API}/admin/sales-reps/${repId}/notes`, { note: newNote }, {});
      setNewNote('');
      onSave();
      // Refresh earnings data to show new note
      const res = await axios.get(`${API}/admin/sales-reps/${repId}/earnings`, {});
      setRepEarnings(res.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); }
  };

  const handleDeleteNote = async (repId, noteId) => {
    try {
      await axios.delete(`${API}/admin/sales-reps/${repId}/notes/${noteId}`, {});
      onSave();
      const res = await axios.get(`${API}/admin/sales-reps/${repId}/earnings`, {});
      setRepEarnings(res.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); }
  };

  const handleSendEmail = async () => {
    if (!emailTarget || !emailForm.subject || !emailForm.body) { alert('Fill all fields'); return; }
    try {
      await axios.post(`${API}/admin/sales-reps/${emailTarget.id}/send-email`, emailForm, {});
      alert('Email sent!');
      setShowEmailModal(false);
      setEmailForm({ subject: '', body: '' });
      setEmailTarget(null);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); alert('Error sending email'); }
  };

  const handleSaveCommission = async () => {
    try {
      const payload = { ...commForm, deal_value: parseFloat(commForm.deal_value) };
      if (!payload.commission_month) delete payload.commission_month;
      await axios.post(`${API}/admin/commissions`, payload, {});
      setShowCommModal(false);
      setCommForm({ sales_rep_id: '', customer_id: '', deal_value: '', commission_month: '', notes: '' });
      onSave();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); alert('Error creating commission'); }
  };

  const handleStatusChange = async (commId, status) => {
    await axios.put(`${API}/admin/commissions/${commId}/status?status=${status}`, {}, {});
    onSave();
  };

  const handleSaveTiers = async () => {
    await axios.put(`${API}/admin/commission-tiers`, tiersForm, {});
    setEditTiers(false);
    onSave();
  };

  const addTier = () => {
    const newTier = tiersForm.length + 1;
    const lastTier = tiersForm[tiersForm.length - 1];
    setTiersForm([...tiersForm, { tier: newTier, name: `Tier ${newTier}`, min_sales: (lastTier?.max_sales || 0) + 1, max_sales: null, commission_rate: 15.0 }]);
  };

  const removeTier = (tierNum) => {
    if (tiersForm.length <= 1) return;
    setTiersForm(tiersForm.filter(t => t.tier !== tierNum).map((t, i) => ({ ...t, tier: i + 1 })));
  };

  const getTierColor = (tier) => tier === 1 ? 'bg-amber-100 text-amber-700' : tier === 2 ? 'bg-slate-200 text-slate-700' : tier === 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700';
  const getTierName = (tier) => { const t = tiersForm.find(x => x.tier === tier); return t ? t.name : `Tier ${tier}`; };

  // Find current admin
  const currentAdmin = salesReps.find(r => r.is_admin);

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Commissions & Sales Reps</h2>
      
      {/* Summary Cards */}
      {commissionSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white border rounded-lg p-4"><p className="text-sm text-slate-500">Total Sales Reps</p><p className="text-2xl font-bold">{commissionSummary.total_sales_reps}</p></div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"><p className="text-sm text-yellow-600">Pending</p><p className="text-2xl font-bold text-yellow-700">${commissionSummary.total_pending?.toFixed(2) || '0.00'}</p></div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4"><p className="text-sm text-blue-600">Approved</p><p className="text-2xl font-bold text-blue-700">${commissionSummary.total_approved?.toFixed(2) || '0.00'}</p></div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4"><p className="text-sm text-green-600">Paid</p><p className="text-2xl font-bold text-green-700">${commissionSummary.total_paid?.toFixed(2) || '0.00'}</p></div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4"><p className="text-sm text-purple-600">Total Override</p><p className="text-2xl font-bold text-purple-700">${commissionSummary.total_override?.toFixed(2) || '0.00'}</p></div>
        </div>
      )}

      {/* Admin Override Configuration */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-purple-800">Admin Override Configuration</h3>
            <p className="text-sm text-purple-600">Admin receives a percentage of ALL sales reps&apos; commissions (based on monthly sales)</p>
          </div>
          <button onClick={() => setShowAdminConfig(!showAdminConfig)} className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded">{showAdminConfig ? 'Cancel' : 'Configure Admin'}</button>
        </div>
        
        {currentAdmin ? (
          <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-purple-200">
            <div className="flex items-center space-x-4">
              <div className="bg-purple-100 p-3 rounded-full"><Star className="text-purple-600" size={24} /></div>
              <div>
                <p className="font-bold text-lg">{getRepName(currentAdmin)}</p>
                <p className="text-sm text-slate-500">{currentAdmin.email}</p>
              </div>
            </div>
            <div className="text-center px-6">
              <p className="text-3xl font-bold text-purple-600">{currentAdmin.override_percentage}%</p>
              <p className="text-xs text-slate-500">Override Rate</p>
            </div>
            <div className="text-center px-6">
              <p className="text-2xl font-bold text-green-600">${(currentAdmin.override_earnings || 0).toFixed(2)}</p>
              <p className="text-xs text-slate-500">Total Override Earnings</p>
            </div>
            <button onClick={() => handleRemoveAdmin(currentAdmin.id)} className="text-red-500 hover:text-red-700"><Trash2 size={20} /></button>
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500">No admin configured. Set an admin to enable commission override.</div>
        )}

        {showAdminConfig && (
          <div className="mt-4 p-4 bg-white rounded-lg border">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Sales Rep to be Admin</label>
                <select value={adminOverride.rep_id} onChange={(e) => setAdminOverride({ ...adminOverride, rep_id: e.target.value })} className="w-full px-3 py-2 border rounded">
                  <option value="">Select rep...</option>
                  {salesReps.map(r => <option key={r.id} value={r.id}>{getRepName(r)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Override Percentage (%)</label>
                <input type="number" step="0.1" value={adminOverride.percentage} onChange={(e) => setAdminOverride({ ...adminOverride, percentage: parseFloat(e.target.value) })} className="w-full px-3 py-2 border rounded" />
              </div>
              <div className="flex items-end">
                <button onClick={handleSetAdmin} className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded">Set as Admin</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Commission Charts */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Monthly Commission Overview</h3>
          <div className="flex space-x-2">
            <button onClick={() => setChartView('monthly')} className={`px-4 py-2 rounded ${chartView === 'monthly' ? 'bg-orange-500 text-white' : 'bg-slate-100'}`}>Monthly</button>
            <button onClick={() => setChartView('yearly')} className={`px-4 py-2 rounded ${chartView === 'yearly' ? 'bg-orange-500 text-white' : 'bg-slate-100'}`}>Yearly</button>
          </div>
        </div>
        
        {chartView === 'monthly' && commissionChartData.monthly?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={commissionChartData.monthly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="display" />
              <YAxis />
              <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
              <Legend />
              <Bar dataKey="total_deals" fill="#3b82f6" name="Total Deals ($)" />
              <Bar dataKey="total_commission" fill="#22c55e" name="Commission ($)" />
            </BarChart>
          </ResponsiveContainer>
        ) : chartView === 'yearly' && commissionChartData.yearly?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={commissionChartData.yearly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
              <Legend />
              <Bar dataKey="total_deals" fill="#3b82f6" name="Total Deals ($)" />
              <Bar dataKey="total_commission" fill="#22c55e" name="Commission ($)" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-slate-400">
            <p>No commission data yet. Record commissions to see the chart.</p>
          </div>
        )}
      </div>

      {/* Commission Tiers */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">{tiersForm.length}-Tier Commission Structure</h3>
            <p className="text-sm text-slate-500">Based on monthly sales performance</p>
          </div>
          <button onClick={() => setEditTiers(!editTiers)} className="text-sm text-blue-600 hover:underline">{editTiers ? 'Cancel' : 'Edit Tiers'}</button>
        </div>
        {editTiers ? (
          <div className="space-y-4">
            {tiersForm.map((tier, idx) => (
              <div key={tier.tier} className="grid grid-cols-6 gap-4 items-center p-3 bg-slate-50 rounded">
                <div><label className="text-xs text-slate-500">Name</label><input type="text" value={tier.name} onChange={(e) => { const t = [...tiersForm]; t[idx].name = e.target.value; setTiersForm(t); }} className="w-full px-2 py-1 border rounded" /></div>
                <div><label className="text-xs text-slate-500">Min Monthly Sales $</label><input type="number" value={tier.min_sales} onChange={(e) => { const t = [...tiersForm]; t[idx].min_sales = parseFloat(e.target.value); setTiersForm(t); }} className="w-full px-2 py-1 border rounded" /></div>
                <div><label className="text-xs text-slate-500">Max Monthly Sales $</label><input type="number" value={tier.max_sales || ''} onChange={(e) => { const t = [...tiersForm]; t[idx].max_sales = e.target.value ? parseFloat(e.target.value) : null; setTiersForm(t); }} className="w-full px-2 py-1 border rounded" placeholder="Unlimited" /></div>
                <div><label className="text-xs text-slate-500">Commission %</label><input type="number" step="0.1" value={tier.commission_rate} onChange={(e) => { const t = [...tiersForm]; t[idx].commission_rate = parseFloat(e.target.value); setTiersForm(t); }} className="w-full px-2 py-1 border rounded" /></div>
                <div className="flex items-end h-full"><button onClick={() => removeTier(tier.tier)} className="text-red-500 hover:text-red-700 p-2" title="Remove tier"><Trash2 size={18} /></button></div>
              </div>
            ))}
            <div className="flex space-x-4">
              <button onClick={addTier} className="bg-blue-500 text-white px-4 py-2 rounded flex items-center"><Plus size={18} className="mr-1" />Add Tier</button>
              <button onClick={handleSaveTiers} className="bg-green-500 text-white px-4 py-2 rounded">Save Tiers</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {commissionTiers.map((tier) => (
              <div key={tier.tier} className={`p-4 rounded-lg border-2 ${tier.tier === 1 ? 'border-amber-300 bg-amber-50' : tier.tier === 2 ? 'border-slate-300 bg-slate-50' : tier.tier === 3 ? 'border-yellow-400 bg-yellow-50' : 'border-purple-300 bg-purple-50'}`}>
                <div className="text-center">
                  <span className={`text-lg font-bold ${getTierColor(tier.tier)} px-3 py-1 rounded`}>{tier.name}</span>
                  <p className="text-3xl font-bold mt-2">{tier.commission_rate}%</p>
                  <p className="text-sm text-slate-500 mt-1">${tier.min_sales.toLocaleString()} - {tier.max_sales ? `$${tier.max_sales.toLocaleString()}` : 'Unlimited'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sales Reps - Expandable Cards */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Sales Representatives</h3>
          <button onClick={() => { setEditingRep(null); resetRepForm(); setShowRepModal(true); }} className="bg-blue-500 text-white px-4 py-2 rounded flex items-center" data-testid="add-sales-rep-btn"><Plus size={18} className="mr-1" />Add Sales Rep</button>
        </div>
        <div className="space-y-4">
          {salesReps.map((rep) => (
            <div key={rep.id} className="border rounded-lg overflow-hidden">
              {/* Rep Header - Always visible */}
              <div className={`flex justify-between items-center p-4 cursor-pointer ${expandedRep === rep.id ? 'bg-blue-50' : 'bg-slate-50 hover:bg-slate-100'}`} onClick={() => handleExpandRep(rep.id)} data-testid={`sales-rep-${rep.id}`}>
                <div className="flex items-center space-x-4">
                  {rep.is_admin && <Star className="text-purple-500" size={20} />}
                  <div>
                    <p className="font-semibold text-lg">{getRepName(rep)}</p>
                    <p className="text-sm text-slate-500">{rep.email} {rep.phone ? `| ${rep.phone}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Monthly Sales</p>
                    <p className="font-bold">${(repMonthlySalesTotals[rep.id] || 0).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Total Commission</p>
                    <p className="font-bold text-green-600">${(rep.total_commission || 0).toFixed(2)}</p>
                  </div>
                  {rep.is_admin && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Override Earnings</p>
                      <p className="font-bold text-purple-600">${(rep.override_earnings || 0).toFixed(2)}</p>
                    </div>
                  )}
                  <span className={`px-3 py-1 rounded ${getTierColor(rep.current_tier || 1)}`}>{getTierName(rep.current_tier || 1)}</span>
                  <span className={`px-2 py-1 text-xs rounded ${rep.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{rep.is_active !== false ? 'Active' : 'Inactive'}</span>
                  <button 
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteRep(rep.id); }} 
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded flex items-center text-sm ml-2"
                    title="Delete Sales Rep"
                    data-testid={`delete-rep-${rep.id}`}
                  >
                    <Trash2 size={16} className="mr-1" /> Delete
                  </button>
                </div>
              </div>
              
              {/* Expanded Content */}
              {expandedRep === rep.id && (
                <div className="border-t bg-white">
                  {/* Sub-tabs */}
                  <div className="flex border-b px-4">
                    {['info', 'employment', 'earnings', 'notes'].map(tab => (
                      <button key={tab} onClick={(e) => { e.stopPropagation(); setActiveRepTab(tab); }} className={`px-4 py-3 text-sm font-medium capitalize ${activeRepTab === tab ? 'border-b-2 border-blue-500 text-blue-600' : 'text-slate-500'}`}>{tab === 'info' ? 'Personal Info' : tab}</button>
                    ))}
                  </div>
                  
                  <div className="p-4">
                    {/* Personal Info Tab */}
                    {activeRepTab === 'info' && (
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center"><Users size={18} className="mr-2" />Personal Information</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Full Name:</span> <span className="font-medium">{getRepName(rep)}</span></p>
                            <p><span className="text-slate-500">SSN:</span> <span className="font-medium">{rep.ssn ? `***-**-${rep.ssn.slice(-4)}` : 'Not provided'}</span></p>
                            <p><span className="text-slate-500">Email:</span> <span className="font-medium">{rep.email}</span></p>
                            <p><span className="text-slate-500">Phone:</span> <span className="font-medium">{rep.phone || 'Not provided'}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3">Address</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Street:</span> <span className="font-medium">{rep.home_address || 'Not provided'}</span></p>
                            <p><span className="text-slate-500">City:</span> <span className="font-medium">{rep.city || '-'}</span></p>
                            <p><span className="text-slate-500">State:</span> <span className="font-medium">{rep.state || '-'}</span></p>
                            <p><span className="text-slate-500">Zip:</span> <span className="font-medium">{rep.zip_code || '-'}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center"><Phone size={18} className="mr-2" />Emergency Contact</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Name:</span> <span className="font-medium">{rep.emergency_contact_name || 'Not provided'}</span></p>
                            <p><span className="text-slate-500">Phone:</span> <span className="font-medium">{rep.emergency_contact_phone || 'Not provided'}</span></p>
                            <p><span className="text-slate-500">Relationship:</span> <span className="font-medium">{rep.emergency_contact_relationship || 'Not provided'}</span></p>
                          </div>
                        </div>
                        <div className="flex flex-col justify-end">
                          <div className="flex space-x-2">
                            <button onClick={(e) => { e.stopPropagation(); setEmailTarget(rep); setShowEmailModal(true); }} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center text-sm"><Mail size={16} className="mr-2" />Send Email</button>
                            <button onClick={(e) => { e.stopPropagation(); setEditingRep(rep); setRepForm({
                              first_name: rep.first_name || '', last_name: rep.last_name || '', email: rep.email, phone: rep.phone || '', ssn: rep.ssn || '',
                              home_address: rep.home_address || '', city: rep.city || '', state: rep.state || '', zip_code: rep.zip_code || '',
                              emergency_contact_name: rep.emergency_contact_name || '', emergency_contact_phone: rep.emergency_contact_phone || '', emergency_contact_relationship: rep.emergency_contact_relationship || '',
                              start_date: rep.start_date || '', quit_date: rep.quit_date || '', is_active: rep.is_active !== false, is_admin: rep.is_admin || false, override_percentage: rep.override_percentage || 0,
                              username: rep.username || '', password: '', role: rep.role || 'sales'
                            }); setShowRepModal(true); }} className="bg-slate-500 hover:bg-slate-600 text-white px-4 py-2 rounded flex items-center text-sm"><Edit size={16} className="mr-2" />Edit</button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteRep(rep.id); }} className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded flex items-center text-sm"><Trash2 size={16} className="mr-2" />Delete</button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Employment Tab */}
                    {activeRepTab === 'employment' && (
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3">Employment Status</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Status:</span> <span className={`font-medium px-2 py-1 rounded ${rep.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{rep.is_active !== false ? 'Active' : 'Inactive'}</span></p>
                            <p><span className="text-slate-500">Start Date:</span> <span className="font-medium">{rep.start_date ? new Date(rep.start_date).toLocaleDateString() : 'Not set'}</span></p>
                            <p><span className="text-slate-500">End/Quit Date:</span> <span className="font-medium">{rep.quit_date ? new Date(rep.quit_date).toLocaleDateString() : 'Currently employed'}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3">Admin Status</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Is Admin:</span> <span className={`font-medium px-2 py-1 rounded ${rep.is_admin ? 'bg-purple-100 text-purple-700' : 'bg-slate-100'}`}>{rep.is_admin ? 'Yes' : 'No'}</span></p>
                            {rep.is_admin && <p><span className="text-slate-500">Override %:</span> <span className="font-medium">{rep.override_percentage}%</span></p>}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Earnings Tab */}
                    {activeRepTab === 'earnings' && repEarnings && (
                      <div>
                        <div className="grid md:grid-cols-3 gap-4 mb-6">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <p className="text-sm text-blue-600">Total Deals</p>
                            <p className="text-2xl font-bold">{repEarnings.jobs?.length || 0}</p>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <p className="text-sm text-green-600">Total Sales</p>
                            <p className="text-2xl font-bold">${(rep.total_sales || 0).toLocaleString()}</p>
                          </div>
                          <div className="bg-orange-50 p-4 rounded-lg">
                            <p className="text-sm text-orange-600">Total Commission</p>
                            <p className="text-2xl font-bold">${(rep.total_commission || 0).toFixed(2)}</p>
                          </div>
                        </div>
                        
                        {/* Monthly Earnings Chart */}
                        {repEarnings.monthly?.length > 0 && (
                          <div className="mb-6">
                            <h4 className="font-semibold mb-3">Monthly Performance</h4>
                            <ResponsiveContainer width="100%" height={200}>
                              <LineChart data={repEarnings.monthly}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="display" />
                                <YAxis />
                                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                                <Legend />
                                <Line type="monotone" dataKey="sales" stroke="#3b82f6" name="Sales" />
                                <Line type="monotone" dataKey="commission" stroke="#22c55e" name="Commission" />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* Recent Jobs */}
                        {repEarnings.jobs?.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-3">Recent Deals</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead><tr className="border-b"><th className="text-left py-2">Date</th><th className="text-left py-2">Customer</th><th className="text-right py-2">Deal Value</th><th className="text-right py-2">Rate</th><th className="text-right py-2">Commission</th></tr></thead>
                                <tbody>
                                  {repEarnings.jobs.slice(0, 10).map((job) => (
                                    <tr key={`${job.date}-${job.customer}`} className="border-b">
                                      <td className="py-2">{new Date(job.date).toLocaleDateString()}</td>
                                      <td className="py-2">{job.customer}</td>
                                      <td className="py-2 text-right">${job.deal_value.toLocaleString()}</td>
                                      <td className="py-2 text-right">{job.rate}%</td>
                                      <td className="py-2 text-right text-green-600 font-medium">${job.commission.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes Tab */}
                    {activeRepTab === 'notes' && (
                      <div>
                        <div className="flex space-x-2 mb-4">
                          <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." className="flex-1 px-3 py-2 border rounded" onClick={(e) => e.stopPropagation()} />
                          <button onClick={(e) => { e.stopPropagation(); handleAddNote(rep.id); }} className="bg-blue-500 text-white px-4 py-2 rounded">Add Note</button>
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {(rep.notes || []).map((note, idx) => (
                            <div key={note.id || idx} className="flex justify-between items-start p-3 bg-slate-50 rounded">
                              <div>
                                <p className="text-sm">{note.note}</p>
                                <p className="text-xs text-slate-400 mt-1">{new Date(note.timestamp).toLocaleString()}</p>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteNote(rep.id, note.id); }} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                            </div>
                          ))}
                          {(!rep.notes || rep.notes.length === 0) && <p className="text-center text-slate-500 py-4">No notes yet.</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {salesReps.length === 0 && <p className="text-center text-slate-500 py-4">No sales reps yet. Add your first sales rep!</p>}
        </div>
      </div>

      {/* Commissions List */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Commission Records</h3>
          <button onClick={() => setShowCommModal(true)} className="bg-green-500 text-white px-4 py-2 rounded flex items-center" data-testid="record-commission-btn"><Plus size={18} className="mr-1" />Record Commission</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-2">Date</th><th className="text-left py-2">Month</th><th className="text-left py-2">Sales Rep</th><th className="text-left py-2">Customer</th><th className="text-right py-2">Deal Value</th><th className="text-right py-2">Rate</th><th className="text-right py-2">Commission</th><th className="text-right py-2">Override</th><th className="text-center py-2">Status</th><th className="text-center py-2">Tier</th></tr></thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.id} className="border-b">
                  <td className="py-2">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="py-2">{c.commission_month || '-'}</td>
                  <td className="py-2">{c.sales_rep_name}</td>
                  <td className="py-2">{c.customer_name}</td>
                  <td className="py-2 text-right">${c.deal_value.toLocaleString()}</td>
                  <td className="py-2 text-right">{c.commission_rate}%</td>
                  <td className="py-2 text-right font-bold text-green-600">${c.commission_amount.toFixed(2)}</td>
                  <td className="py-2 text-right text-purple-600">{c.admin_override_amount > 0 ? `$${c.admin_override_amount.toFixed(2)}` : '-'}</td>
                  <td className="py-2 text-center">
                    <select value={c.status} onChange={(e) => handleStatusChange(c.id, e.target.value)} className={`px-2 py-1 rounded text-xs ${c.status === 'pending' ? 'bg-yellow-100' : c.status === 'approved' ? 'bg-blue-100' : 'bg-green-100'}`}>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="paid">Paid</option>
                    </select>
                  </td>
                  <td className="py-2 text-center"><span className={`px-2 py-1 rounded text-xs ${getTierColor(c.tier_at_time)}`}>{getTierName(c.tier_at_time)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {commissions.length === 0 && <p className="text-center text-slate-500 py-8">No commission records yet.</p>}
        </div>
      </div>

      {/* Sales Rep Modal - Comprehensive */}
      {showRepModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editingRep ? 'Edit Sales Rep' : 'Add Sales Rep'}</h3>
            
            {/* Personal Info */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-600 mb-3 border-b pb-2">Personal Information</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">First Name *</label><input type="text" value={repForm.first_name} onChange={(e) => setRepForm({ ...repForm, first_name: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">Last Name *</label><input type="text" value={repForm.last_name} onChange={(e) => setRepForm({ ...repForm, last_name: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">SSN</label><input type="text" value={repForm.ssn} onChange={(e) => setRepForm({ ...repForm, ssn: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="xxx-xx-xxxx" /></div>
                <div><label className="block text-sm font-medium mb-1">Email *</label><input type="email" value={repForm.email} onChange={(e) => setRepForm({ ...repForm, email: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">Phone</label><input type="tel" value={repForm.phone} onChange={(e) => setRepForm({ ...repForm, phone: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
              </div>
            </div>
            
            {/* Address */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-600 mb-3 border-b pb-2">Address</h4>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Street Address</label><input type="text" value={repForm.home_address} onChange={(e) => setRepForm({ ...repForm, home_address: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">City</label><input type="text" value={repForm.city} onChange={(e) => setRepForm({ ...repForm, city: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">State</label><input type="text" value={repForm.state} onChange={(e) => setRepForm({ ...repForm, state: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">Zip Code</label><input type="text" value={repForm.zip_code} onChange={(e) => setRepForm({ ...repForm, zip_code: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
              </div>
            </div>
            
            {/* Emergency Contact */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-600 mb-3 border-b pb-2">Emergency Contact</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">Contact Name</label><input type="text" value={repForm.emergency_contact_name} onChange={(e) => setRepForm({ ...repForm, emergency_contact_name: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">Contact Phone</label><input type="tel" value={repForm.emergency_contact_phone} onChange={(e) => setRepForm({ ...repForm, emergency_contact_phone: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">Relationship</label><input type="text" value={repForm.emergency_contact_relationship} onChange={(e) => setRepForm({ ...repForm, emergency_contact_relationship: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., Spouse, Parent" /></div>
              </div>
            </div>
            
            {/* Employment */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-600 mb-3 border-b pb-2">Employment</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">Start Date</label><input type="date" value={repForm.start_date} onChange={(e) => setRepForm({ ...repForm, start_date: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div><label className="block text-sm font-medium mb-1">End/Quit Date</label><input type="date" value={repForm.quit_date} onChange={(e) => setRepForm({ ...repForm, quit_date: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
                <div className="flex items-center pt-6"><label className="flex items-center cursor-pointer"><input type="checkbox" checked={repForm.is_active} onChange={(e) => setRepForm({ ...repForm, is_active: e.target.checked })} className="mr-2" /><span>Active Employee</span></label></div>
              </div>
            </div>

            {/* Login Credentials */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-blue-600 mb-3 border-b pb-2">Login Credentials</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Username *</label>
                  <input type="text" value={repForm.username} onChange={(e) => setRepForm({ ...repForm, username: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="e.g., john.doe" />
                  <p className="text-xs text-slate-500 mt-1">Auto-generated if left blank</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{editingRep ? 'New Password' : 'Password *'}</label>
                  <input type="password" value={repForm.password} onChange={(e) => setRepForm({ ...repForm, password: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder={editingRep ? 'Leave blank to keep current' : 'Enter password'} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Role</label>
                  <select value={repForm.role} onChange={(e) => setRepForm({ ...repForm, role: e.target.value })} className="w-full px-3 py-2 border rounded">
                    <option value="sales">Sales Rep</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button onClick={() => { setShowRepModal(false); setEditingRep(null); resetRepForm(); }} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={handleSaveRep} className="px-4 py-2 bg-blue-500 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Commission Modal */}
      {showCommModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Record Commission</h3>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium mb-1">Sales Rep *</label>
                <select value={commForm.sales_rep_id} onChange={(e) => setCommForm({ ...commForm, sales_rep_id: e.target.value })} className="w-full px-3 py-2 border rounded">
                  <option value="">Select sales rep...</option>
                  {salesReps.map((r) => <option key={r.id} value={r.id}>{getRepName(r)}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Customer *</label>
                <select value={commForm.customer_id} onChange={(e) => setCommForm({ ...commForm, customer_id: e.target.value })} className="w-full px-3 py-2 border rounded">
                  <option value="">Select customer...</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Deal Value ($) *</label><input type="number" value={commForm.deal_value} onChange={(e) => setCommForm({ ...commForm, deal_value: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="50000" /></div>
              <div><label className="block text-sm font-medium mb-1">Commission Month (optional)</label><input type="month" value={commForm.commission_month} onChange={(e) => setCommForm({ ...commForm, commission_month: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium mb-1">Notes</label><textarea value={commForm.notes} onChange={(e) => setCommForm({ ...commForm, notes: e.target.value })} className="w-full px-3 py-2 border rounded" rows="2"></textarea></div>
            </div>
            <div className="flex justify-end space-x-3 mt-6"><button onClick={() => setShowCommModal(false)} className="px-4 py-2 border rounded">Cancel</button><button onClick={handleSaveCommission} className="px-4 py-2 bg-green-500 text-white rounded">Save</button></div>
          </div>
        </div>
      )}

      {/* Bonus Section */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <DollarSign className="mr-2 text-green-500" size={20} />
            Sales Bonuses
          </h3>
          <button 
            onClick={() => setShowBonusModal(true)} 
            className="bg-green-500 text-white px-4 py-2 rounded flex items-center"
            data-testid="add-bonus-btn"
          >
            <Plus size={18} className="mr-1" />Add Bonus
          </button>
        </div>
        
        {/* Bonus Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-600">Total Bonuses Paid</p>
            <p className="text-xl font-bold text-green-700">
              ${totalBonusesPaid.toLocaleString()}
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-600">Number of Bonuses</p>
            <p className="text-xl font-bold text-blue-700">{bonuses.length}</p>
          </div>
        </div>

        {/* Bonus List */}
        {bonuses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3">Sales Rep</th>
                  <th className="text-left py-2 px-3">Amount</th>
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Reason</th>
                  <th className="text-left py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bonuses.map((bonus) => (
                  <tr key={bonus.id} className="border-b hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium">{bonus.sales_rep_name}</td>
                    <td className="py-2 px-3 text-green-600 font-bold">${bonus.amount?.toLocaleString()}</td>
                    <td className="py-2 px-3">{bonus.date}</td>
                    <td className="py-2 px-3">{bonus.reason}</td>
                    <td className="py-2 px-3">
                      <button 
                        onClick={() => handleDeleteBonus(bonus.sales_rep_id, bonus.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete Bonus"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-slate-500 text-center py-4">No bonuses recorded yet. Click &ldquo;Add Bonus&rdquo; to reward your sales team!</p>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && emailTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Send Email to {getRepName(emailTarget)}</h3>
            <p className="text-sm text-slate-500 mb-4">{emailTarget.email}</p>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium mb-1">Subject *</label><input type="text" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium mb-1">Body *</label><textarea value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} className="w-full px-3 py-2 border rounded" rows="6"></textarea></div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => { setShowEmailModal(false); setEmailTarget(null); setEmailForm({ subject: '', body: '' }); }} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={handleSendEmail} className="px-4 py-2 bg-blue-500 text-white rounded">Send Email</button>
            </div>
          </div>
        </div>
      )}

      {/* Bonus Modal */}
      {showBonusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <DollarSign className="mr-2 text-green-500" />Add Bonus
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Sales Rep *</label>
                <select 
                  value={bonusForm.sales_rep_id} 
                  onChange={(e) => setBonusForm({ ...bonusForm, sales_rep_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Select Sales Rep</option>
                  {salesReps.map(rep => (
                    <option key={rep.id} value={rep.id}>{getRepName(rep)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount ($) *</label>
                <input 
                  type="number" 
                  value={bonusForm.amount} 
                  onChange={(e) => setBonusForm({ ...bonusForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="1000"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date *</label>
                <input 
                  type="date" 
                  value={bonusForm.date} 
                  onChange={(e) => setBonusForm({ ...bonusForm, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reason *</label>
                <textarea 
                  value={bonusForm.reason} 
                  onChange={(e) => setBonusForm({ ...bonusForm, reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                  placeholder="e.g., Q4 Performance Bonus, Monthly Top Seller, Special Achievement..."
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => { setShowBonusModal(false); setBonusForm({ sales_rep_id: '', amount: '', date: '', reason: '' }); }} 
                className="px-4 py-2 border rounded"
              >
                Cancel
              </button>
              <button 
                onClick={handleAddBonus} 
                className="px-4 py-2 bg-green-500 text-white rounded"
              >
                Add Bonus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionsTab;
